import { DEFAULT_DASHBOARDS_OPTIONS } from "constants/RBACConstants";
import { Config } from "hooks/useSearchResultWidgetTableDataSource";
import { cloneDeep, isString, toUpper } from "lodash";
import { makeCancellableAPIRequest } from "restAPI";
import { CustomDashboardListRequestDto } from "types/api/CustomDashboardListRequestDto";
import { CustomDashboardSequenceUpdateRequestDto } from "types/api/CustomDashboardSequenceUpdateRequestDto";
import { BaseResponseDto } from "types/api/baseResponseDto";
import { CustomDashboardJpaDto } from "types/api/customDashboardJpaDto";
import { CustomDashboardListResponseDto } from "types/api/customDashboardListResponseDto";
import { DashboardExportSchemaV1_0 } from "types/dashboardExportSchema";
import { FilterInfo } from "types/pagination";
import { COMMON_DASHBOARD_TYPES } from "views/Dashboards/OutOfBoxDashboardConfigs";

import GlobalSettingAPI from "./GlobalSettingAPI";

type ExportAdvancedSearchCsvPayload = {
  widgetConfig: Config;
  timezone?: string;
  dashboardWidgetsFilterRequestDto: {
    filters: FilterInfo;
  };
};

type CVEAndCWEDetailPayload = {
  type: string;
  vulnerabilities: string[];
};

const trimString = (data: any) => {
  if (isString(data.name)) {
    data.name = data.name.trim();
  }
  // widgets is from dashboard json import
  (data.widgetConfigJsonNode || data.widgets).forEach((widget: any) => {
    if (isString(widget.config.title)) {
      widget.config.title = widget.config.title.trim();
    }
    if (isString(widget.config.subtitle)) {
      widget.config.subtitle = widget.config.subtitle.trim();
    }
  });

  return data;
};

const CustomDashboardAPI = {
  create(data: CustomDashboardJpaDto) {
    return makeCancellableAPIRequest<CustomDashboardJpaDto>(
      "POST",
      "/api/custom-dashboard",
      {
        data: trimString(data),
      },
    );
  },
  importJson(data: DashboardExportSchemaV1_0) {
    return makeCancellableAPIRequest<BaseResponseDto<CustomDashboardJpaDto>>(
      "POST",
      "/api/custom-dashboard/import",
      {
        data: trimString(data),
      },
    );
  },
  update(data: CustomDashboardJpaDto) {
    return makeCancellableAPIRequest<CustomDashboardJpaDto>(
      "PUT",
      `/api/custom-dashboard/${data.id}`,
      {
        data,
      },
    );
  },
  updateWidgetConfig(dashboardId: number, widgetConfig: Record<string, any>) {
    return makeCancellableAPIRequest<CustomDashboardJpaDto>(
      "PUT",
      `/api/custom-dashboard/${dashboardId}/widget`,
      {
        data: widgetConfig,
      },
    );
  },
  getAllCustomDashboards(params: CustomDashboardListRequestDto = {}) {
    return makeCancellableAPIRequest<CustomDashboardListResponseDto[]>(
      "GET",
      "/api/custom-dashboard",
      {
        params,
      },
    );
  },
  getAll() {
    const request = GlobalSettingAPI.getCustomizeDefaultDashboards();
    const { promise, cancel } = makeCancellableAPIRequest(
      "GET",
      "/api/custom-dashboard",
    );

    const _promise = Promise.all([promise, request.promise]).then(
      ([dashboards, oobDashboardData]: any) => {
        return cloneDeep([...COMMON_DASHBOARD_TYPES, ...dashboards]).map(
          (d) => {
            if (oobDashboardData?.dashboardNames?.[toUpper(d.id)]) {
              d.name = oobDashboardData.dashboardNames[toUpper(d.id)];
            }
            return d;
          },
        );
      },
    );
    return {
      cancel: () => {
        cancel();
        request.cancel();
      },
      promise: _promise,
    };
  },
  get(id: number) {
    return makeCancellableAPIRequest<CustomDashboardJpaDto>(
      "GET",
      `/api/custom-dashboard/${id}`,
    );
  },
  delete(id: number) {
    return makeCancellableAPIRequest<void>(
      "DELETE",
      `/api/custom-dashboard/${id}`,
    );
  },
  validateName(name: string, idToIgnore: number) {
    return makeCancellableAPIRequest<void>(
      "GET",
      `/api/custom-dashboard/validate`,
      {
        params: {
          name,
          idToIgnore,
        },
      },
      false,
    );
  },
  getAllPublicDashbards(defaultDashboardOptions = DEFAULT_DASHBOARDS_OPTIONS) {
    const request = GlobalSettingAPI.getCustomizeDefaultDashboards();
    const { promise, cancel } = makeCancellableAPIRequest<
      CustomDashboardListResponseDto[]
    >("GET", `/api/custom-dashboard/public`);

    const _promise = Promise.all([promise, request.promise]).then(
      ([dashboards, oobDashboardData]) => {
        return cloneDeep([...defaultDashboardOptions, ...dashboards]).map(
          (d) => {
            if (oobDashboardData?.dashboardNames?.[toUpper(String(d.id))]) {
              d.name = oobDashboardData.dashboardNames[toUpper(String(d.id))];
            }
            d.id = d.id + "";
            return d;
          },
        );
      },
    );

    return {
      cancel: () => {
        cancel();
        request.cancel();
      },
      promise: _promise,
    };
  },
  updateSequence(data: CustomDashboardSequenceUpdateRequestDto) {
    return makeCancellableAPIRequest<void>(
      "PUT",
      `/api/custom-dashboard/sequence`,
      {
        data,
      },
    );
  },
  exportAdvancedSearchCsv(payload: ExportAdvancedSearchCsvPayload) {
    return makeCancellableAPIRequest<BaseResponseDto<{ message: string }>>(
      "POST",
      "/api/widgets/advancedSearch/export",
      {
        data: payload,
      },
    );
  },
  getCVEAndCWEDetail(payload: CVEAndCWEDetailPayload) {
    return makeCancellableAPIRequest(
      "POST",
      "api/compliance/vulnerability/details",
      {
        data: payload,
      },
    );
  },
};

export default CustomDashboardAPI;
